import { Component, OnInit, Inject, OnDestroy, HostListener } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Unsubscribe } from 'redux';

import { APP_CONFIG } from '../../../api/config';

import { IAppState, IAppConfig, Actions } from '../../../store';
import {
  Reservation, MessageCategory, Hotel, ReservationErrorMessageKeys, ReservationStayPreferences, MessageType
} from '../../../api/model';
import { ActionHandler } from '../../../store/action.handler';
import { AppAction } from '../../../store/interfaces/IAppState';
import { MessagesUtil } from '../../../common/messages.util';
import { IMessage } from '../../../api/interfaces/Messages/IMessage';
import { scrollTo } from '../../../common/common';
import { ViewScrollService } from '../../../api/services/view-scroll.service';
import { PageSectionType } from '../../../common/model/page/page-section.model';
import { SiteURLUtils } from '../../../common/site.url.utils';
import { MediaQueryService } from '../../../common/media-query/media-query.service';
import { RBFInteractionService } from '../../../api/services/rbf-interaction.service';
import { ApplicationRouteNames } from '../../../common/model/application-route-names.model';
import { CancelReservationDelegate, ModifyReservationDelegate, ModifyAccountDelegate } from '../../../api/delegates';
import { TrackingJsonService } from '../../../api/services';
import { RouteNames } from '../../../api/enums';
import { AccountStayPreferences } from '../../../api/model/user/stay-preferences/stay-preference.model';

@Component({
  selector: 'stay-mgmt-stay-pref',
  templateUrl: './stay-pref.component.html',
  styleUrls: ['./stay-pref.component.scss']
})
export class StayPrefComponent implements OnInit, OnDestroy {
  error: any = null;
  reservation: Reservation = null;
  lastName: string;
  reservationId: string;
  hotel: Hotel = null;
  message: IMessage;
  state: IAppState;
  messages: { [key: string]: string };
  messageCategory: MessageCategory;
  unsubscribe: Unsubscribe;
  mobileApp: boolean;
  isEditable: boolean;
  isExtraSmallViewPort: boolean;

  stayPreferences: ReservationStayPreferences = null;
  acceptTerms: boolean;
  accountStayPreferences: AccountStayPreferences = null;

  alertMessages: Map<MessageCategory, IMessage>;

  constructor(
    @Inject(APP_CONFIG) protected appConfig: IAppConfig,
    private _ngRedux: NgRedux<IAppState>,
    private _cancelRes: CancelReservationDelegate,
    private _modifyReservationDelegate: ModifyReservationDelegate,
    private _modifyAccountDelegate: ModifyAccountDelegate,
    private _activatedRoute: ActivatedRoute,
    private _titleService: Title,
    private _trackingJson: TrackingJsonService,
    private _actions: Actions,
    private _viewScrollService: ViewScrollService,
    private _mediaQueryService: MediaQueryService,
    private _rbfInteractionService: RBFInteractionService
  ) {
    this._activatedRoute.params.subscribe(params => {
      this.reservationId = params['id'];
      this.lastName = params['lastName'];
    });
    this._activatedRoute.queryParams.subscribe(params => {
      this.mobileApp = params.mobileApp === 'true';
    });
    this._titleService.setTitle('IHG | Room and Stay Preferences');
    this.verifyViewport();
  }

  ngOnInit() {
    this.alertMessages = new Map<MessageCategory, IMessage>();
    this.message = null;
    this._trackingJson.subscribeRoute(RouteNames.StayPreferences);
    this.updateFromState();
    this.unsubscribe = this._ngRedux.subscribe(() => {
      this.updateFromState();
    });

    if (this.reservation === null) {
      //  THIS MAKES ALL THE CALLS TO APIS TO POPULATE STATE
      this._cancelRes.fetchReservation(this.reservationId, this.lastName, RouteNames.StayPreferences);
    }
    if (this.stayPreferences === null && this.reservation) {
      this._modifyReservationDelegate.fetchStayPreferences(this.reservation, this.mobileApp);
    }

    if (this.accountStayPreferences === null) {
      this._modifyAccountDelegate.fetchAccountStayPreferences();
    }
    this._modifyReservationDelegate.fetchMember();
    scrollTo(null, true);
  }

  getMessageKeys() {
    return this.alertMessages.keys();
  }

  updateFromState() {
    this.state = this._ngRedux.getState();
    if (!this.state) {
      return;
    }
    this.messages = MessagesUtil.filterTranslations(this.state.translations, 'staypreferences');

    this.reservation = this.state.reservation;
    this.stayPreferences = this.state.stayPreferences;
    this.hotel = this.state.hotel;
    this.isEditable = !(this.stayPreferences && this.stayPreferences.editable === false);
    if (this.isEditable === false) {
      this.message = <IMessage>{ key: ReservationErrorMessageKeys.PREFERENCES_NOT_EDITABLE, messageType: MessageType.ERROR };
    }

    ActionHandler.handleError(this.state, AppAction.GET_RESERVATION, () => {
      this.error = this.state.error;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.RETRIEVE_ERROR_UNKNOWN_KEY);
    });
    ActionHandler.handleError(this.state, AppAction.MODIFY_STAY_PREFERENCES, () => {
      this.error = this.state.error;
      this.message = MessagesUtil.fromError(this.appConfig, this.error, ReservationErrorMessageKeys.RETRIEVE_ERROR_UNKNOWN_KEY);
      this._viewScrollService.scrollToSection(PageSectionType.PAGE_MESSAGING_MODULE);
    });

    ActionHandler.handleSuccess(this.state, AppAction.MODIFY_STAY_PREFERENCES, () => {
      if (this.mobileApp) {
        this.message = <IMessage>{ key: ReservationErrorMessageKeys.SUCCESS_SAVE_PREFERENCES, messageType: MessageType.SUCCESS };
        this._viewScrollService.scrollToSection(PageSectionType.PAGE_MESSAGING_MODULE);
      } else {
        SiteURLUtils.redirectToReservationView(this.appConfig, this.reservationId, this.lastName);
      }
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_RESERVATION, () => {
      if (this.stayPreferences === null) {
        this._modifyReservationDelegate.fetchStayPreferences(this.reservation, this.mobileApp);
      }
    });
    ActionHandler.handleSuccess(this.state, AppAction.GET_STAY_PREFERENCES, () => {
      this.stayPreferences = this.state.stayPreferences;
      this.isEditable = !!(this.stayPreferences && this.stayPreferences.editable === true);
      if (this.isEditable === false) {
        this.message = <IMessage>{ key: ReservationErrorMessageKeys.PREFERENCES_NOT_EDITABLE, messageType: MessageType.ERROR };
      }
      this._modifyReservationDelegate.fetchPointsEstimate(this.reservation);
    });
    ActionHandler.handleError(this.state, AppAction.GET_STAY_PREFERENCES, () => {
      this._modifyReservationDelegate.fetchPointsEstimate(this.reservation);
    });
    ActionHandler.handleSuccess(this.state, AppAction.ASSOCIATE_RESERVATION, () => {
      this._handleUpdatedReservation();
    });
  }

  private _handleUpdatedReservation() {
    if (this.lastName !== this.reservation.guest.lastName) {
      this._rbfInteractionService.resetPayInteraction(this.reservation.confirmationNumber, this.reservation.guest.lastName, ApplicationRouteNames.STAY_PREFERENCES);
    }
  }

  verifyViewport() {
    this.isExtraSmallViewPort = this._mediaQueryService.isExtraSmallViewPort();
  }

  displayMessage(messageDetails) {
    this.alertMessages.set(messageDetails.category, messageDetails.message);
    if (!!messageDetails.message) {
      this._viewScrollService.scrollToSection(PageSectionType.PAGE_MESSAGING_MODULE);
    }
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    this.verifyViewport();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.verifyViewport();
  }

  ngOnDestroy() {
    this._trackingJson.unsubscribeRoute(RouteNames.StayPreferences);
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

}



// WEBPACK FOOTER //
// ./src/main/webapp/stay-mgmt/app/routes/stay-pref/stay-pref/stay-pref.component.ts